import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useMutation} from '@apollo/client';
import StickyHeadTable, {
  ColumnProps,
} from 'components/StickyHeadTable/StickyHeadTable';
import {useTranslation} from 'react-i18next';
import {
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import BlankCard from 'components/BlankCard/BlankCard';
import Modal from 'components/Modal/Modal';
import {Alpha3Code} from 'i18n-iso-countries';
import {alertContext} from 'contextApi/context/error';
import {formatDate, parseDate} from 'commons/dates';
import {formatDateByLocale} from 'commons/formatDate';
import {
  CREATE_VACATION_REGULATION,
  DELETE_VACATION_REGULATION,
  UPDATE_VACATION_REGULATION,
} from 'graphql/admin/Mutation';
import {
  VacationRegulationProps,
  VacationRegulationRangeProps,
} from 'models/configuration';
import './VacationDaysParamsTable.scss';

type MutationErrorLike = {
  graphQLErrors?: Array<{message?: string}>;
  networkError?: {
    result?: {
      errors?: Array<{message?: string}>;
    };
  };
  message?: string;
};

type VacationRegulationRangeForm = {
  fromYear: string;
  toYear: string;
  vacationDays: string;
};

type VacationParameterTableRow = VacationRegulationRangeProps & {
  rowIndex: number;
};

type VacationParameterModalState = {
  mode: 'create' | 'edit';
  regulation: VacationRegulationProps;
  initialRange: VacationRegulationRangeForm;
  rangeIndex?: number;
} | null;

interface VacationDaysParamsTableProps {
  country: Alpha3Code;
  fetch: () => Promise<void>;
  vacationRegulations?: VacationRegulationProps[];
}

interface VacationParameterModalProps {
  open: boolean;
  title: string;
  submitLabel: string;
  initialRange: VacationRegulationRangeForm;
  isYearRangeEditable: boolean;
  onClose: () => void;
  onSubmit: (payload: VacationRegulationRangeForm) => Promise<void>;
}

interface VacationRegulationCreateModalProps {
  open: boolean;
  title: string;
  submitLabel: string;
  initialEffectiveFrom: string;
  minEffectiveFrom: string;
  onClose: () => void;
  onSubmit: (effectiveFrom: string) => Promise<void>;
}

interface VacationRegulationDeleteModalProps {
  open: boolean;
  title: string;
  description: string;
  submitLabel: string;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const createEmptyRangeForm = (): VacationRegulationRangeForm => ({
  fromYear: '',
  toYear: '',
  vacationDays: '',
});

const mapRangeToMutationInput = (
  range: VacationRegulationRangeProps
): VacationRegulationRangeProps => ({
  fromYear: Number(range.fromYear),
  toYear: Number(range.toYear),
  vacationDays: Number(range.vacationDays),
});

const mapRangesToMutationInput = (
  ranges: VacationRegulationRangeProps[] = []
): VacationRegulationRangeProps[] => {
  return ranges.map(mapRangeToMutationInput);
};

const mapRangeToForm = (
  range?: VacationRegulationRangeProps
): VacationRegulationRangeForm => {
  if (!range) {
    return createEmptyRangeForm();
  }

  return {
    fromYear: String(range.fromYear),
    toYear: String(range.toYear),
    vacationDays: String(range.vacationDays),
  };
};

const getDateInputValue = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  return value.slice(0, 10);
};

const getDisplayDate = (value?: string | null, language?: string): string => {
  const normalizedValue = getDateInputValue(value);

  if (!normalizedValue) {
    return '-';
  }

  const parsedDate = parseDate(normalizedValue);
  return language
    ? formatDateByLocale(parsedDate, language as 'en' | 'es' | 'fr' | 'pt')
    : formatDate(parsedDate, 'dd/MM/yyyy');
};

const addDaysToDateInput = (value: string, days: number): string => {
  const nextDate = new Date(`${value}T00:00:00.000Z`);
  nextDate.setUTCDate(nextDate.getUTCDate() + days);
  return nextDate.toISOString().slice(0, 10);
};

const getTomorrowDateInput = (): string => {
  return addDaysToDateInput(new Date().toISOString().slice(0, 10), 1);
};

const getFirstDayOfNextYearInput = (): string => {
  const currentYear = new Date().getUTCFullYear();

  return new Date(Date.UTC(currentYear + 1, 0, 1)).toISOString().slice(0, 10);
};

const getDefaultSelectedRegulationId = (
  regulations: VacationRegulationProps[]
): number | undefined => {
  return (
    regulations.find(item => item.isEditable)?.id ||
    regulations.find(item => item.isCurrent)?.id ||
    regulations[0]?.id
  );
};

const VacationParameterModal: FC<VacationParameterModalProps> = ({
  open,
  title,
  submitLabel,
  initialRange,
  isYearRangeEditable,
  onClose,
  onSubmit,
}) => {
  const {t} = useTranslation();
  const [range, setRange] = useState(initialRange);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRange(initialRange);
    }
  }, [initialRange, open]);

  const handleChange = (
    field: keyof VacationRegulationRangeForm,
    value: string
  ) => {
    setRange(previousValue => ({
      ...previousValue,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(range);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      small
      open={open}
      title={title}
      onClose={onClose}
      className="VacationParameterModal"
    >
      <div className="flex flex-col gap-4 pt-4">
        <Typography variant="subtitle1">
          {t(
            'leaves.VacationDaysParamsTable.yearRangeLabel',
            'Rango de años cumplidos'
          )}
        </Typography>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextField
            label={t('leaves.VacationDaysParamsTable.fromYear', 'Desde')}
            type="number"
            value={range.fromYear}
            placeholder="0"
            onChange={event => handleChange('fromYear', event.target.value)}
            inputProps={{
              min: 1,
              readOnly: !isYearRangeEditable,
              disabled: !isYearRangeEditable,
            }}
          />
          <TextField
            label={t('leaves.VacationDaysParamsTable.toYear', 'Hasta')}
            type="number"
            value={range.toYear}
            placeholder="0"
            onChange={event => handleChange('toYear', event.target.value)}
            inputProps={{
              min: 1,
              readOnly: !isYearRangeEditable,
              disabled: !isYearRangeEditable,
            }}
          />
        </div>

        <TextField
          fullWidth
          label={t('leaves.VacationDaysParamsTable.days')}
          type="number"
          value={range.vacationDays}
          placeholder="0"
          onChange={event => handleChange('vacationDays', event.target.value)}
          inputProps={{min: 1, step: 'any'}}
        />

        <div className="flex justify-end gap-3">
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            {t('leaves.AddParamModal.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const VacationRegulationDeleteModal: FC<VacationRegulationDeleteModalProps> = ({
  open,
  title,
  description,
  submitLabel,
  onClose,
  onSubmit,
}) => {
  const {t} = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      small
      open={open}
      title={title}
      description={description}
      onClose={onClose}
      className="VacationRegulationDeleteModal"
    >
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          {t('leaves.AddParamModal.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {submitLabel}
        </Button>
      </div>
    </Modal>
  );
};

const VacationRegulationCreateModal: FC<VacationRegulationCreateModalProps> = ({
  open,
  title,
  submitLabel,
  initialEffectiveFrom,
  minEffectiveFrom,
  onClose,
  onSubmit,
}) => {
  const {t} = useTranslation();
  const [effectiveFrom, setEffectiveFrom] = useState(initialEffectiveFrom);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setEffectiveFrom(initialEffectiveFrom);
    }
  }, [initialEffectiveFrom, open]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(effectiveFrom);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      small
      open={open}
      title={title}
      onClose={onClose}
      className="VacationRegulationCreateModal"
    >
      <div className="flex flex-col gap-4 pt-4">
        <TextField
          fullWidth
          type="date"
          label={t('leaves.VacationDaysParamsTable.dateLabel')}
          value={effectiveFrom}
          onChange={event => setEffectiveFrom(event.target.value)}
          InputLabelProps={{shrink: true}}
          inputProps={{
            min: minEffectiveFrom,
            'data-testid': 'create-vacation-regulation-date',
          }}
        />

        <div className="flex justify-end gap-3">
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            {t('leaves.AddParamModal.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface RangeValidationError {
  key: string;
  fallback: string;
}

const findInvalidRange = (
  ranges: VacationRegulationRangeProps[]
): RangeValidationError | null => {
  for (let index = 0; index < ranges.length; index += 1) {
    const current = ranges[index];
    if (current.toYear < current.fromYear) {
      return {
        key: 'leaves.VacationDaysParamsTable.rangeYears',
        fallback: 'Cada rango debe tener un fin mayor o igual al inicio.',
      };
    }
    if (current.vacationDays <= 0) {
      return {
        key: 'leaves.VacationDaysParamsTable.rangeDays',
        fallback: 'Cada rango debe tener una cantidad de días mayor a cero.',
      };
    }
    if (index > 0 && ranges[index - 1].toYear + 1 !== current.fromYear) {
      return {
        key: 'leaves.VacationDaysParamsTable.rangeContinuity',
        fallback: 'Los rangos deben ser continuos y no pueden traslaparse.',
      };
    }
  }
  return null;
};

/**
 * Replaces the legacy threshold editor with a regulation-centric UI that lets
 * admins inspect historical regulations and edit only future scheduled ones.
 *
 * @param props - Country code, refetch callback, and regulation history for the selected country
 * @returns The vacation regulations management section.
 */
/**
 * Renders vacation regulations and the parameter editor used by management.
 * Keeps scheduled-regulation actions attached to the regulation row while the
 * lower table focuses on the parameters of the selected regulation.
 *
 * @param country - Country whose vacation regulations are being managed
 * @param fetch - Callback used to refresh configuration data after mutations
 * @param vacationRegulations - Regulations available for the selected country
 * @returns Vacation regulation tables and management modals
 */
const VacationDaysParamsTable: FC<VacationDaysParamsTableProps> = ({
  country,
  fetch,
  vacationRegulations = [],
}) => {
  const {t, i18n} = useTranslation();
  const {dispatch} = useContext(alertContext);
  const [selectedRegulationId, setSelectedRegulationId] = useState<
    number | undefined
  >(() => getDefaultSelectedRegulationId(vacationRegulations));
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [regulationPendingDeletion, setRegulationPendingDeletion] =
    useState<VacationRegulationProps | null>(null);
  const [parameterModalState, setParameterModalState] =
    useState<VacationParameterModalState>(null);
  const [createVacationRegulation] = useMutation(CREATE_VACATION_REGULATION);
  const [deleteVacationRegulation] = useMutation(DELETE_VACATION_REGULATION);
  const [updateVacationRegulation] = useMutation(UPDATE_VACATION_REGULATION);
  const defaultCreateEffectiveFrom = useMemo(
    () => getFirstDayOfNextYearInput(),
    []
  );
  const minCreateEffectiveFrom = useMemo(() => getTomorrowDateInput(), []);

  useEffect(() => {
    if (!vacationRegulations.length) {
      setSelectedRegulationId(undefined);
      return;
    }

    if (
      selectedRegulationId === undefined ||
      !vacationRegulations.some(item => item.id === selectedRegulationId)
    ) {
      setSelectedRegulationId(
        getDefaultSelectedRegulationId(vacationRegulations)
      );
    }
  }, [selectedRegulationId, vacationRegulations]);

  const selectedRegulation = useMemo(
    () =>
      vacationRegulations.find(item => item.id === selectedRegulationId) ??
      vacationRegulations[0],
    [selectedRegulationId, vacationRegulations]
  );
  const hasEditableRegulation = vacationRegulations.some(
    item => item.isEditable
  );
  const canCreateRegulation =
    vacationRegulations.length > 0 && !hasEditableRegulation;
  const selectedRegulationRows = useMemo<VacationParameterTableRow[]>(
    () =>
      selectedRegulation?.vacationRanges.map((item, rowIndex) => ({
        ...item,
        rowIndex,
      })) ?? [],
    [selectedRegulation]
  );

  const showErrorMessage = useCallback(
    (message: string) => {
      dispatch({
        type: 'show',
        payload: {
          message,
          level: 'error',
          duration: 'short',
          cancelable: true,
        },
      });
    },
    [dispatch]
  );

  const showMutationError = useCallback(
    (error: unknown) => {
      const mutationError = error as MutationErrorLike;

      showErrorMessage(
        mutationError?.graphQLErrors?.[0]?.message ||
          mutationError?.networkError?.result?.errors?.[0]?.message ||
          mutationError?.message ||
          t('common.generic_error', 'Algo salió mal')
      );
    },
    [showErrorMessage, t]
  );

  const validateVacationRanges = useCallback(
    (
      ranges: VacationRegulationRangeProps[]
    ): VacationRegulationRangeProps[] | null => {
      const normalizedRanges = [...mapRangesToMutationInput(ranges)].sort(
        (left, right) => left.fromYear - right.fromYear
      );

      const hasNonNumbers = normalizedRanges.some(
        item =>
          Number.isNaN(item.fromYear) ||
          Number.isNaN(item.toYear) ||
          Number.isNaN(item.vacationDays)
      );

      if (normalizedRanges.length === 0) {
        showErrorMessage(
          t(
            'leaves.VacationDaysParamsTable.rangeRequired',
            'Debes capturar al menos un parámetro de vacaciones.'
          )
        );
        return null;
      }
      if (hasNonNumbers) {
        showErrorMessage(
          t(
            'leaves.VacationDaysParamsTable.rangeNumbers',
            'Todos los campos de los parámetros deben ser numéricos.'
          )
        );
        return null;
      }
      if (normalizedRanges[0].fromYear !== 1) {
        showErrorMessage(
          t(
            'leaves.VacationDaysParamsTable.rangeStartsAtOne',
            'El primer rango debe iniciar en el año 1.'
          )
        );
        return null;
      }

      const invalidRange = findInvalidRange(normalizedRanges);
      if (invalidRange) {
        showErrorMessage(t(invalidRange.key, invalidRange.fallback));
        return null;
      }

      return normalizedRanges;
    },
    [showErrorMessage, t]
  );

  const handleDeleteParameter = useCallback(
    async (rowIndex: number) => {
      if (!selectedRegulation?.isEditable) {
        return;
      }

      const lastRangeIndex = selectedRegulation.vacationRanges.length - 1;

      if (rowIndex !== lastRangeIndex) {
        showErrorMessage(
          t(
            'leaves.VacationDaysParamsTable.deleteLastRangeHint',
            'Sólo se puede eliminar el último parámetro para mantener consistencia entre los rangos existentes.'
          )
        );
        return;
      }

      const nextRanges = selectedRegulation.vacationRanges.filter(
        (_item, index) => index !== rowIndex
      );
      const validatedRanges =
        nextRanges.length === 0 ? [] : validateVacationRanges(nextRanges);

      if (validatedRanges === null) {
        return;
      }

      try {
        await updateVacationRegulation({
          variables: {
            id: selectedRegulation.id,
            effectiveFrom: getDateInputValue(selectedRegulation.effectiveFrom),
            vacationRanges: validatedRanges,
          },
        });
        await fetch();
        setSelectedRegulationId(selectedRegulation.id);
        setParameterModalState(null);
      } catch (error) {
        showMutationError(error);
      }
    },
    [
      fetch,
      selectedRegulation,
      showErrorMessage,
      showMutationError,
      t,
      updateVacationRegulation,
      validateVacationRanges,
    ]
  );

  const getStatusLabel = (regulation: VacationRegulationProps) => {
    if (regulation.isCurrent) {
      return t('leaves.VacationDaysParamsTable.currentStatus', 'Vigente');
    }

    if (regulation.isEditable) {
      return t('leaves.VacationDaysParamsTable.upcomingStatus', 'Programada');
    }

    return t('leaves.VacationDaysParamsTable.historicalStatus', 'Histórica');
  };

  const renderStatusChip = (regulation: VacationRegulationProps) => {
    let color: 'success' | 'warning' | 'default' = 'default';
    if (regulation.isCurrent) {
      color = 'success';
    } else if (regulation.isEditable) {
      color = 'warning';
    }

    return (
      <Chip color={color} label={getStatusLabel(regulation)} size="small" />
    );
  };

  const tableRows = useMemo(
    () =>
      vacationRegulations.map(item => ({
        ...item,
        parametersCount: item.vacationRanges.length,
      })),
    [vacationRegulations]
  );

  const regulationColumns: ColumnProps[] = [
    {
      id: 'effectiveFrom',
      label: t('leaves.VacationDaysParamsTable.effectiveFrom', 'Vigente desde'),
      render: (value: string) => getDisplayDate(value, i18n.language),
    },
    {
      id: 'effectiveTo',
      label: t('leaves.VacationDaysParamsTable.effectiveTo', 'Vigente hasta'),
      render: (value: string | null, row: VacationRegulationProps) =>
        value ? getDisplayDate(value, i18n.language) : getStatusLabel(row),
    },
    {
      id: 'status',
      label: t('leaves.VacationDaysParamsTable.status', 'Estado'),
      render: (_value: unknown, row: VacationRegulationProps) =>
        renderStatusChip(row),
    },
    {
      id: 'parametersCount',
      label: t('leaves.VacationDaysParamsTable.parameters', 'Parámetros'),
    },
    {
      id: 'id',
      label: t('leaves.VacationDaysParamsTable.actions', 'Acciones'),
      align: 'right',
      render: (_value: unknown, row: VacationRegulationProps) => (
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {row.isEditable && (
            <IconButton
              aria-label={t(
                'leaves.VacationDaysParamsTable.deleteRegulation',
                'Eliminar regulación'
              )}
              color="error"
              data-testid={`vacation-regulation-delete-${row.id}`}
              onClick={() => setRegulationPendingDeletion(row)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          )}
          <IconButton
            aria-label={t(
              'leaves.VacationDaysParamsTable.viewDetails',
              'Ver detalle'
            )}
            data-testid={`vacation-regulation-view-${row.id}`}
            onClick={() => setSelectedRegulationId(row.id)}
          >
            <VisibilityOutlinedIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const rangeColumns = useMemo<ColumnProps[]>(() => {
    const baseColumns: ColumnProps[] = [
      {
        id: 'fromYear',
        label: t('leaves.VacationDaysParamsTable.fromYear', 'Desde'),
      },
      {
        id: 'toYear',
        label: t('leaves.VacationDaysParamsTable.toYear', 'Hasta'),
      },
      {
        id: 'vacationDays',
        label: t('leaves.VacationDaysParamsTable.days'),
      },
    ];

    if (!selectedRegulation?.isEditable) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        id: 'rowIndex',
        label: t('leaves.VacationDaysParamsTable.actions', 'Acciones'),
        align: 'right',
        render: (_value: unknown, row: VacationParameterTableRow) => (
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            {row.rowIndex === selectedRegulationRows.length - 1 && (
              <IconButton
                aria-label={t(
                  'leaves.VacationDaysParamsTable.removeRange',
                  'Eliminar parámetro'
                )}
                color="error"
                data-testid={`vacation-parameter-delete-${row.rowIndex}`}
                onClick={() => handleDeleteParameter(row.rowIndex)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            )}
            <IconButton
              aria-label={t(
                'leaves.VacationDaysParamsTable.editParameterModalTitle',
                'Editar parámetro de vacaciones'
              )}
              data-testid={`vacation-parameter-edit-${row.rowIndex}`}
              onClick={() =>
                setParameterModalState({
                  mode: 'edit',
                  regulation: selectedRegulation,
                  rangeIndex: row.rowIndex,
                  initialRange: mapRangeToForm(row),
                })
              }
            >
              <EditIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];
  }, [
    handleDeleteParameter,
    selectedRegulation,
    selectedRegulationRows.length,
    t,
  ]);

  const handleCreateRegulation = async (effectiveFrom: string) => {
    if (vacationRegulations.length === 0) {
      return;
    }

    if (!effectiveFrom) {
      showErrorMessage(
        t(
          'leaves.VacationDaysParamsTable.dateRequired',
          'Debes seleccionar una fecha de inicio.'
        )
      );
      return;
    }

    try {
      const response = await createVacationRegulation({
        variables: {
          country,
          effectiveFrom,
          vacationRanges: [],
        },
      });
      const createdRegulation = response.data?.createVacationRegulation;

      await fetch();

      if (createdRegulation) {
        setSelectedRegulationId(createdRegulation.id);
      }

      setIsCreateModalOpen(false);
    } catch (error) {
      showMutationError(error);
    }
  };

  const handleDeleteRegulation = async () => {
    if (!regulationPendingDeletion) {
      return;
    }

    try {
      await deleteVacationRegulation({
        variables: {
          id: regulationPendingDeletion.id,
        },
      });
      await fetch();
      setSelectedRegulationId(undefined);
      setRegulationPendingDeletion(null);
    } catch (error) {
      showMutationError(error);
    }
  };

  const handleSubmitParameter = async (
    payload: VacationRegulationRangeForm
  ) => {
    if (!parameterModalState) {
      return;
    }

    const nextRange: VacationRegulationRangeProps = {
      fromYear: Number(payload.fromYear),
      toYear: Number(payload.toYear),
      vacationDays: Number(payload.vacationDays),
    };
    const baseRanges = parameterModalState.regulation.vacationRanges.filter(
      (_item, index) => index !== parameterModalState.rangeIndex
    );
    const validatedRanges = validateVacationRanges([...baseRanges, nextRange]);

    if (!validatedRanges) {
      return;
    }

    try {
      await updateVacationRegulation({
        variables: {
          id: parameterModalState.regulation.id,
          effectiveFrom: getDateInputValue(
            parameterModalState.regulation.effectiveFrom
          ),
          vacationRanges: validatedRanges,
        },
      });
      await fetch();
      setSelectedRegulationId(parameterModalState.regulation.id);
      setParameterModalState(null);
    } catch (error) {
      showMutationError(error);
    }
  };

  const getRegulationHintText = (): string => {
    if (vacationRegulations.length === 0) {
      return t(
        'leaves.VacationDaysParamsTable.noBaseRegulation',
        'Este país aún no tiene una regulación base para programar una nueva.'
      );
    }
    if (hasEditableRegulation) {
      return t(
        'leaves.VacationDaysParamsTable.pendingRegulation',
        'Ya existe una regulación futura pendiente. Edítala antes de crear otra.'
      );
    }
    return t(
      'leaves.VacationDaysParamsTable.futureOnlyHint',
      'Sólo las regulaciones futuras pueden editarse desde esta pantalla.'
    );
  };

  return (
    <div className="VacationDaysParamsTable w-full flex flex-col flex-wrap">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Typography color="text.secondary" variant="body2">
          {getRegulationHintText()}
        </Typography>
        <Button
          data-testid="add-vacation-regulation"
          disabled={!canCreateRegulation}
          onClick={() => setIsCreateModalOpen(true)}
          startIcon={<AddIcon />}
          variant="outlined"
        >
          {t(
            'leaves.VacationDaysParamsTable.addRegulation',
            'Añadir nueva regulación'
          )}
        </Button>
      </div>

      <StickyHeadTable
        columns={regulationColumns}
        hidePagination
        isLoading={false}
        onPageChange={() => {}}
        placeholder={t(
          'leaves.VacationDaysParamsTable.emptyRegulations',
          'No hay regulaciones registradas para este país.'
        )}
        rows={tableRows}
      />

      {selectedRegulation && (
        <BlankCard
          className="mt-6"
          title={t(
            'leaves.VacationDaysParamsTable.detailTitle',
            'Parámetros de la regulación'
          )}
          description={`${getDisplayDate(
            selectedRegulation.effectiveFrom,
            i18n.language
          )} - ${
            selectedRegulation.effectiveTo
              ? getDisplayDate(selectedRegulation.effectiveTo, i18n.language)
              : getStatusLabel(selectedRegulation)
          }`}
        >
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {renderStatusChip(selectedRegulation)}
              {selectedRegulation.isEditable && (
                <Chip
                  color="info"
                  label={t(
                    'leaves.VacationDaysParamsTable.editableBadge',
                    'Editable'
                  )}
                  size="small"
                  variant="outlined"
                />
              )}
            </div>

            {selectedRegulation.isEditable && (
              <div className="flex flex-wrap justify-end gap-3">
                <Button
                  data-testid="add-vacation-parameter"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={() =>
                    setParameterModalState({
                      mode: 'create',
                      regulation: selectedRegulation,
                      initialRange: createEmptyRangeForm(),
                    })
                  }
                >
                  {t(
                    'leaves.VacationDaysParamsTable.addRange',
                    'Agregar parámetro'
                  )}
                </Button>
              </div>
            )}
          </div>

          {selectedRegulation.isEditable && selectedRegulationRows.length > 0 && (
            <div className="mb-4">
              <Typography color="text.secondary" variant="body2">
                {t(
                  'leaves.VacationDaysParamsTable.deleteLastRangeHint',
                  'Sólo se puede eliminar el último parámetro para mantener consistencia entre los rangos existentes.'
                )}
              </Typography>
            </div>
          )}

          <StickyHeadTable
            columns={rangeColumns}
            hidePagination
            isLoading={false}
            onPageChange={() => {}}
            placeholder={t('common.empty_list')}
            rows={selectedRegulationRows}
          />
        </BlankCard>
      )}

      <VacationRegulationCreateModal
        initialEffectiveFrom={defaultCreateEffectiveFrom}
        minEffectiveFrom={minCreateEffectiveFrom}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateRegulation}
        open={isCreateModalOpen}
        submitLabel={t(
          'leaves.VacationDaysParamsTable.saveRegulation',
          'Guardar regulación'
        )}
        title={t(
          'leaves.VacationDaysParamsTable.addModalTitle',
          'Nueva regulación de vacaciones'
        )}
      />

      <VacationRegulationDeleteModal
        description={t(
          'leaves.VacationDaysParamsTable.deleteRegulationDescription',
          'Esta acción eliminará la regulación programada seleccionada.'
        )}
        onClose={() => setRegulationPendingDeletion(null)}
        onSubmit={handleDeleteRegulation}
        open={Boolean(regulationPendingDeletion)}
        submitLabel={t(
          'leaves.VacationDaysParamsTable.confirmDeleteRegulation',
          'Eliminar'
        )}
        title={t(
          'leaves.VacationDaysParamsTable.deleteRegulationTitle',
          'Eliminar regulación programada'
        )}
      />

      <VacationParameterModal
        initialRange={
          parameterModalState?.initialRange ?? createEmptyRangeForm()
        }
        isYearRangeEditable={parameterModalState?.mode !== 'edit'}
        onClose={() => setParameterModalState(null)}
        onSubmit={handleSubmitParameter}
        open={Boolean(parameterModalState)}
        submitLabel={t(
          parameterModalState?.mode === 'edit'
            ? 'leaves.VacationDaysParamsTable.updateParameter'
            : 'leaves.VacationDaysParamsTable.createParameter',
          parameterModalState?.mode === 'edit'
            ? 'Guardar parámetro'
            : 'Crear parámetro'
        )}
        title={t(
          parameterModalState?.mode === 'edit'
            ? 'leaves.VacationDaysParamsTable.editParameterModalTitle'
            : 'leaves.VacationDaysParamsTable.parameterModalTitle',
          parameterModalState?.mode === 'edit'
            ? 'Editar parámetro de vacaciones'
            : 'Nuevo parámetro de vacaciones'
        )}
      />
    </div>
  );
};

export default VacationDaysParamsTable;
